import type { AxiosResponse } from "axios";
import apiClient from "./client";
import type {
  MasterType,
  UserMasterDownloadResult,
  UserMasterPreviewResponse,
  UserMasterUploadResponse,
} from "../types/user-master";

type ApiErrorBody = {
  message?: string;
};

export const parseContentDispositionFilename = (
  contentDisposition: string | undefined
): string | undefined => {
  if (!contentDisposition) {
    return undefined;
  }
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) {
    return decodeURIComponent(utf8Match[1]);
  }
  const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return asciiMatch ? asciiMatch[1] : undefined;
};

export const parseUserMasterDownloadResponse = async (
  response: AxiosResponse<Blob>
): Promise<UserMasterDownloadResult> => {
  const blob = response.data;
  if (blob.type === "application/json") {
    const text = await blob.text();
    try {
      const parsed = JSON.parse(text) as ApiErrorBody;
      return {
        status: "ng",
        message: parsed.message || "ダウンロードに失敗しました",
      };
    } catch {
      return { status: "ng", message: "ダウンロードに失敗しました" };
    }
  }

  const header =
    response.headers["content-disposition"] ||
    response.headers["Content-Disposition"];
  const params = response.config.params as { master_type?: MasterType } | undefined;
  const filename =
    parseContentDispositionFilename(header) ||
    (params?.master_type === "tok" ? "特約店.xlsx" : "COM.xlsx");

  return { status: "ok", blob, filename };
};

export const downloadUserMaster = async (
  masterType: MasterType
): Promise<AxiosResponse<Blob>> => {
  return apiClient.get<Blob>("/user-master-download", {
    params: { master_type: masterType },
    responseType: "blob",
  });
};

export const previewUserMaster = async (
  file: File,
  masterType: MasterType
): Promise<UserMasterPreviewResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("master_type", masterType);
  const { data } = await apiClient.post<UserMasterPreviewResponse>(
    "/user-master-preview",
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
  return data;
};

export const uploadUserMaster = async (
  file: File,
  masterType: MasterType
): Promise<UserMasterUploadResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("master_type", masterType);
  const { data } = await apiClient.post<UserMasterUploadResponse>(
    "/user-master-upload",
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
  return data;
};
